import React, { FC, createContext, useCallback, useEffect, useState } from 'react';

import { InvoiceTabEnum } from 'containers/invoices/create-invoice/constants';
import { GenericError, GenericModal, Loader } from 'components';
import { useCreateInvoiceMutation, useLazyGetInvoicePDFPreviewQuery } from 'containers/invoices/api';
import DateUtils from 'utils/dateUtils';
import { useAppDispatch, useAppSelector } from 'hooks';

import { useNavigate } from 'react-router-dom';
import { selectCurrentTeam, showNotifier } from 'containers/app/appSlice';
import {
  checkIfAnyPaymentModeIsEligible,
  getAttachmentArray,
  getLineItemsArrayForEvent,
  getServiceChargeEventPayload,
  hasChangesToSync,
  prepareDiscountRelatedPayload,
  prepareLineItemList,
  prepareServiceChargeListRequestPayload,
  prepareSummatedUniqueTaxList
} from 'containers/invoices/utils';
import { InvoicePayload, TaxesAndFeesResponse } from 'containers/invoices/invoices.model';
import { ErrorResponse } from 'containers/app/app.model';
import { APIErrorMsg, APIErrorStatuses } from 'services/type';
import { NotifierTypes } from 'constants/notifierConstants';
import { constructArrayObjectAsString, throttle } from 'utils/commonUtils';
import { InvoicesRouteState } from 'routes/types';
import routesPath from 'routes/RoutesPath';
import { InvoiceSourceEnum, InvoiceVersion } from '../constants';
import { API_SOURCE, invoiceMinimumAmount } from 'constants/common';
import BillToSection from '../components/BillToSection';
import InvoiceLineItems from '../components/InvoiceLineItems';
import NotesSection from '../components/NotesSection';
import TermsSection from '../components/TermsSection';
import AttachmentsSection from '../components/AttachmentsSection';
import TotalSection from '../components/TotalSection';
import { formatAmount } from 'utils/amountUtils';
import TeamDetailsSection from '../components/TeamDetailsSection';
import InvoiceHeader from '../components/InvoiceHeader';
import {
  resetInvoiceState,
  selectAppliedLineItemLevelTaxes,
  selectDiscountFormData,
  selectDiscountSettings,
  selectInvoice,
  selectInvoiceAttachments,
  selectInvoiceCustomer,
  selectInvoiceFinalAmount,
  selectInvoiceFromJobNumber,
  selectInvoiceLineItems,
  selectInvoiceNotes,
  selectInvoiceSubtotal,
  selectIsInvoiceFromJob,
  selectIsServiceFeeEnabled,
  selectIsSignatureEnabled,
  selectSelectedServiceCharge,
  updateInvoiceCreatedResponse
} from '../InvoicesSlice';
import { GetTeamProfileApiResponse } from 'containers/team-profile/teamProfile.model';
import { InvoiceErrorFieldsDefault } from '../create-invoice/types';
import { selectCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from '../events';
import { CrossIconvector } from 'assets/icons';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import { useCreateInvoiceFromJobMutation } from 'containers/jobs/api';

interface ICreateInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

export const CreateInvoiceContext = createContext<ICreateInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

enum PageState {
  BUYER_VIEW = 'BUYER_VIEW',
  PDF_VIEW = 'PDF_VIEW'
}

interface BuyersPreviewProps {
  goBack: () => void;
  getTeamProfileData: GetTeamProfileApiResponse;
  taxesAndFees: TaxesAndFeesResponse;
}

const BuyersPreview: FC<BuyersPreviewProps> = ({ goBack, getTeamProfileData, taxesAndFees }) => {
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>(PageState.BUYER_VIEW);

  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [getInvoicePDFPreview, getInvoicePDFPreviewQueryState] = useLazyGetInvoicePDFPreviewQuery();
  const {
    data: getInvoicePDFPreviewResponse,
    isSuccess: getPreviewSuccess,
    isFetching: isPDFPreviewFetching,
    isError: isPDFPreviewError
  } = getInvoicePDFPreviewQueryState;

  const selectedServiceCharge = useAppSelector(selectSelectedServiceCharge);
  const selectedCustomerForInvoice = useAppSelector(selectInvoiceCustomer);
  const selectedLineItemsForInvoice = useAppSelector(selectInvoiceLineItems);
  const selectDiscountSettingsForInvoice = useAppSelector(selectDiscountSettings);
  const selectDiscountFormDataForInvoice = useAppSelector(selectDiscountFormData);
  const selectInvoiceAttachmentsForInvoice = useAppSelector(selectInvoiceAttachments);
  const selectInvoiceNotesForInvoice = useAppSelector(selectInvoiceNotes);
  const isServiceFeeEnabled = useAppSelector(selectIsServiceFeeEnabled);
  const selectedAppliedLineItemLevelTaxes = useAppSelector(selectAppliedLineItemLevelTaxes);
  const invoiceNumber = 'IN-XXXXX';

  const selectInvoiceSubtotalForInvoice = useAppSelector(selectInvoiceSubtotal);
  const selectInvoiceFinalAmountForInvoice = useAppSelector(selectInvoiceFinalAmount);
  const selectedInvoice = useAppSelector(selectInvoice);
  const selectedIsSignatureEnabled = useAppSelector(selectIsSignatureEnabled);
  const customizations = useAppSelector(selectCustomizations);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJob);
  const invoiceFromJobNumber = useAppSelector(selectInvoiceFromJobNumber);

  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });

  const queryParams = new URLSearchParams(window.location.search);

  const jobId = queryParams.get('jobId');

  const dispatch = useAppDispatch();

  const [
    createInvoiceFromJobMutation,
    {
      isSuccess: isCreateInvoiceFromJobSuccess,
      data: invoiceFromJobCreatedResponse,
      isError: isCreateInvoiceFromJobError,
      error: createInvoiceFromJobError,
      isLoading: isCreateInvoiceFromJobLoading
    }
  ] = useCreateInvoiceFromJobMutation();

  const [
    createInvoiceMutation,
    {
      isSuccess: isCreateInvoiceSuccess,
      data: invoiceCreatedResponse,
      isError: isCreateInvoiceError,
      error: createInvoiceError,
      isLoading: isCreateInvoiceLoading
    }
  ] = useCreateInvoiceMutation();

  useEffect(() => {
    if (isCreateInvoiceSuccess || isCreateInvoiceFromJobSuccess) {
      dispatch(resetInvoiceState());
      dispatch(
        updateInvoiceCreatedResponse(
          isCreateInvoiceSuccess ? invoiceCreatedResponse : invoiceFromJobCreatedResponse.data
        )
      );
      const invoiceRouteState: InvoicesRouteState = {
        showShareModal: true,
        isEditInvoiceShare: false,
        showSidePanel: false
      };
      navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
    }
  }, [isCreateInvoiceSuccess]);

  useEffect(() => {
    if (isCreateInvoiceError || isCreateInvoiceFromJobError) {
      const error = isCreateInvoiceError
        ? (createInvoiceError as ErrorResponse)
        : (createInvoiceFromJobError as ErrorResponse);
      if (error.status === APIErrorStatuses.STATUS_422) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.message,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else if (error.status === APIErrorStatuses.STATUS_429) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: APIErrorMsg.LIMIT_EXCEEDED,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Failed to create invoice',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
    }
  }, [isCreateInvoiceError]);

  const getPaymentModes = () => {
    const selectedModes = selectedInvoice.invoicePaymentModes.filter(item => item.isSelected).map(item => item.id);

    const updatedPaymentModes = customizations.paymentModes.map(paymentMode => {
      if (paymentMode.teamLevelVisiblity) {
        if (selectedModes.includes(paymentMode.id)) {
          return {
            id: paymentMode.id,
            enabled: true
          };
        }
        return {
          id: paymentMode.id,
          enabled: false
        };
      }
      return {
        id: paymentMode.id,
        enabled: paymentMode.enabled
      };
    });
    return updatedPaymentModes;
  };

  const getCreateInvoicePayload = () => {
    const serviceChargeListPayload = prepareServiceChargeListRequestPayload(selectedInvoice.selectedServiceCharge);

    const invoicePayload: InvoicePayload = {
      version: InvoiceVersion.THREE_POINT_ZERO,
      teamId: currentTeam.id.toString(),
      storeName: currentTeam.storeName,
      // store all level amounts in redux slice eg: subtotal, finalamount etc
      amount: selectedInvoice.invoiceSubTotal.toString(),
      comment: selectedInvoice.comment,
      customerId: selectedInvoice?.invoiceCustomer?.ID,
      lineItems: prepareLineItemList(selectedInvoice.invoiceLineItems),
      ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
      surchargePercentage: selectedInvoice.isServiceFeeEnabled
        ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
        : '0',
      isServiceFeeEnabled: selectedInvoice.isServiceFeeEnabled,
      attachments: getAttachmentArray(selectedInvoice),
      tipEnabled: selectedInvoice.tipEnabled,
      tncId: taxesAndFees.tnc.enabled ? taxesAndFees.tnc.tncId : 0,
      ...prepareSummatedUniqueTaxList({
        appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
        invoiceLineItems: selectedInvoice.invoiceLineItems,
        selectedServiceCharge: selectedInvoice.selectedServiceCharge
      }),
      serviceCharges: serviceChargeListPayload,
      serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : '0',
      source: InvoiceSourceEnum.WEB,
      isSignatureEnabled: selectedIsSignatureEnabled,
      serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? null,
      paymentModes: getPaymentModes()
    };
    const serviceChargeEventPayload = getServiceChargeEventPayload(serviceChargeListPayload);
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoiceSave, {
      customerName: selectedInvoice?.invoiceCustomer?.name,
      customerId: selectedInvoice?.invoiceCustomer?.ID,
      teamId: currentTeam.id.toString(),
      storeName: currentTeam.storeName,
      amount: selectedInvoice.invoiceSubTotal.toString(),
      'Line Items': constructArrayObjectAsString(
        getLineItemsArrayForEvent(selectedInvoice.invoiceLineItems),
        'Line item'
      ),
      ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
      surchargePercentage: selectedInvoice.isServiceFeeEnabled
        ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
        : '0',
      ...(serviceChargeEventPayload?.length > 0 ? { serviceCharges: serviceChargeEventPayload } : {}),
      serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? ''
    });
    return invoicePayload;
  };

  const onCreateAndShare = () => {
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItems.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    if (selectedInvoice.invoiceCustomer === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    if (selectedInvoice.invoiceSubTotal < (+currentTeam?.minimumAmount || invoiceMinimumAmount)) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModes.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        customizations.paymentModes,
        selectedInvoice.finalAmount
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    if (isPageIncomplete) {
      setShowInvoiceErrorsPopup({ open: true, errors });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomer,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItems,
        selectedInvoice.initialInvoiceLineItems,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotal,
        selectedInvoice.initialInvoiceSubTotal,
        selectedInvoice.isServiceFeeEnabled,
        selectedInvoice.isServiceFeeEnabledInitial,
        selectedInvoice.currentSurcharge,
        selectedInvoice.initialSurcharge
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else {
      createInvoiceMutation(getCreateInvoicePayload());
    }
  };

  const createAndShareHandler = useCallback(throttle(onCreateAndShare, 800), [selectedInvoice, isInvoiceFromJob]);

  useEffect(() => {
    if (getPreviewSuccess) {
      const pdf = new Blob([getInvoicePDFPreviewResponse], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdf);
      setPreviewUrl(url);
    }
  }, [getPreviewSuccess]);

  const renderDate = () => {
    let date = DateUtils.getToday();
    date = DateUtils.getDateInFormat({ date: new Date(date), dateFormat: 'dd MMM yyyy' });
    return date;
  };

  const handleCreateInvoiceFromJob = () => {
    createInvoiceFromJobMutation({
      teamId: currentTeam.id,
      jobId: jobId,
      body: { jobInvoiceRequest: getCreateInvoicePayload(), jobNumber: invoiceFromJobNumber, source: API_SOURCE.WEB }
    });
  };

  const handleOnCreateInvoiceWithoutSelfCheckout = () => {
    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomer,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItems,
        selectedInvoice.initialInvoiceLineItems,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotal,
        selectedInvoice.initialInvoiceSubTotal,
        selectedInvoice.isServiceFeeEnabled,
        selectedInvoice.isServiceFeeEnabledInitial,
        selectedInvoice.currentSurcharge,
        selectedInvoice.initialSurcharge
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else {
      createInvoiceMutation(getCreateInvoicePayload());
    }
  };

  const handleOnEditPaymentMethods = () => {
    handleCloseNoSelfCheckoutPopup();
    goBack();
  };

  const handleCloseNoSelfCheckoutPopup = () => {
    setShowNoSelfCheckoutPopup(false);
  };

  const handleCloseInvoiceErrorsPopup = () => {
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
  };

  const onPDFPreviewClick = () => {
    if (!previewUrl) {
      const serviceChargeListPayload = prepareServiceChargeListRequestPayload(selectedInvoice.selectedServiceCharge);
      const invoicePayloadPreview = {
        version: InvoiceVersion.THREE_POINT_ZERO,
        invoiceNumber: invoiceNumber,
        teamId: currentTeam.id.toString(),
        storeName: currentTeam.storeName,
        amount: selectedInvoice.invoiceSubTotal.toString(),
        comment: selectedInvoice.comment,
        customerId: selectedInvoice?.invoiceCustomer?.ID,
        lineItems: prepareLineItemList(selectedInvoice.invoiceLineItems),
        discount:
          selectedInvoice.discountFormData.discountInCurrency === '0'
            ? null
            : selectedInvoice.discountFormData.discountInCurrency,
        discountPercentage: selectedInvoice.discountFormData.discountPercent,
        attachments: getAttachmentArray(selectedInvoice),
        tipEnabled: selectedInvoice.tipEnabled,
        surchargePercentage: selectedInvoice.isServiceFeeEnabled
          ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
          : '0',
        timeStamp: new Date(),
        tncID: taxesAndFees.tnc.enabled ? taxesAndFees.tnc.tncId : 0,
        createdDate: new Date(),
        ...prepareSummatedUniqueTaxList({
          appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
          invoiceLineItems: selectedInvoice.invoiceLineItems,
          selectedServiceCharge: selectedInvoice.selectedServiceCharge
        }),
        serviceCharges: serviceChargeListPayload,
        serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : '0',
        isInvoiceSignatureEnabled: selectedIsSignatureEnabled,
        serviceAddressId: selectedInvoice?.invoiceCustomer?.serviceAddress?.[0]?.id ?? null
      };
      getInvoicePDFPreview(invoicePayloadPreview);
    }
    setPageState(PageState.PDF_VIEW);
  };

  return (
    <CreateInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      <div className="flex h-full w-full flex-col border">
        <InvoiceHeader
          title="Create & Share"
          pageState={pageState}
          setPageState={setPageState}
          onPDFPreviewClick={onPDFPreviewClick}
          editAndShareHandler={createAndShareHandler}
          goBack={goBack}
          isActionLoading={isCreateInvoiceLoading || isCreateInvoiceFromJobLoading}
        />

        {pageState === PageState.BUYER_VIEW && (
          <div className="flex grow gap-8 bg-borderGray px-[149px] pt-8 pb-[102px]">
            <div className="flex w-[675px] flex-col gap-[30px]">
              <BillToSection customer={selectedCustomerForInvoice} />

              <InvoiceLineItems
                items={selectedLineItemsForInvoice}
                billAmount={selectInvoiceSubtotalForInvoice}
                surChargeAmount={+selectedInvoice.currentSurcharge?.valueInCurrencyWithBps || 0}
                serviceFeeEnabled={isServiceFeeEnabled}
                serviceCharges={selectedServiceCharge}
                taxes={selectedAppliedLineItemLevelTaxes}
                discountData={selectDiscountFormDataForInvoice}
                totalAmount={selectInvoiceFinalAmountForInvoice}
                selectDiscountSettingsForInvoice={selectDiscountSettingsForInvoice}
              />

              {selectInvoiceNotesForInvoice && <NotesSection notes={selectInvoiceNotesForInvoice} />}

              {taxesAndFees.tnc.enabled && <TermsSection terms={taxesAndFees.tnc.tnc} />}

              {selectInvoiceAttachmentsForInvoice && selectInvoiceAttachmentsForInvoice.length > 0 && (
                <AttachmentsSection attachments={selectInvoiceAttachmentsForInvoice} />
              )}
            </div>
            <div className="flex w-[435px] flex-col gap-[30px]">
              <TotalSection
                total={formatAmount(selectInvoiceFinalAmountForInvoice)}
                date={renderDate()}
                dueDate={renderDate()}
              />
              <TeamDetailsSection
                teamName={currentTeam.name}
                teamPhone={getTeamProfileData.phone}
                teamEmail={getTeamProfileData.email}
                teamBillingAddress={getTeamProfileData.address}
                teamWebsite={getTeamProfileData.website}
              />
            </div>
          </div>
        )}

        {pageState === PageState.PDF_VIEW && (
          <>
            {previewUrl && (
              <div>
                <object
                  style={{
                    height: window.innerHeight,
                    width: window.innerWidth
                  }}
                  data={`${previewUrl}#toolbar=0`}
                  type="application/pdf"></object>
              </div>
            )}
            {isPDFPreviewFetching && <Loader />}
            {isPDFPreviewError && <GenericError handleErrorBack={() => setPageState(PageState.BUYER_VIEW)} />}
          </>
        )}
      </div>
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title="Save and sync changes to the related job?"
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={`Changes in your invoices will be applied to the associated job ${invoiceFromJobNumber}.`}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleCreateInvoiceFromJob}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => <CrossIconvector height={18} width={18} title="sync-popup-close" />}
          isActionInProgress={isCreateInvoiceFromJobLoading}
          height="h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={false}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isCreateInvoiceLoading || isCreateInvoiceFromJobLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={+currentTeam?.maximumAmount}
          minimumAmount={+currentTeam?.minimumAmount}
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
    </CreateInvoiceContext.Provider>
  );
};

export default BuyersPreview;
